import * as React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDeleteParkingGateway } from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useDeleteParkingGateway';
import {
    type ParkingGateways,
    useGetParkingGateways,
} from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useParkingGateways';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import AddParkingGateway from 'src/features/parkingManagement/components/EntryExitGateways/AddParkingGateway';
import DeleteParkingGatewayModal from 'src/features/parkingManagement/components/EntryExitGateways/DeleteParkingGatewayModal';
import EditParkingGatewayModal from 'src/features/parkingManagement/components/EntryExitGateways/EditParkingGatewayModal';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { EntryExitPointsTableColumns } from 'src/features/parkingManagement/utils/entryExitPointsTableColumns';
import { GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { DataTable } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalErrorMessages } from 'src/utils/messages';

const EntryExitPoints = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });
    const { setTitle, removeTitle } = useTitleStore();
    const [selectedParkingGateway, setSelectedParkingGateway] = React.useState<ParkingGateways | undefined>(undefined);

    const [isDeleteParkingGatewayModalOpen, setIsDeleteParkingGatewayModalOpen] = React.useState(false);
    const [isEditParkingGatewayModalOpen, setIsEditParkingGatewayModalOpen] = React.useState(false);

    const { data: parkingGateways, refetch } = useGetParkingGateways({
        parking: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const tableData: ParkingGateways[] = React.useMemo(() => {
        return (parkingGateways?.results ?? []).filter((item): item is ParkingGateways => item.parking === parkingId);
    }, [parkingGateways]);

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const handleParkingGatewayModal = (type: 'edit' | 'delete', gateway?: ParkingGateways, isOpen: boolean = false) => {
        setSelectedParkingGateway(gateway);
        if (type === 'delete') {
            setIsDeleteParkingGatewayModalOpen(isOpen);
        } else {
            setIsEditParkingGatewayModalOpen(isOpen);
        }
    };

    const openDeleteParkingGatewayModal = (gateway: ParkingGateways) =>
        handleParkingGatewayModal('delete', gateway, true);
    const closeDeleteParkingGatewayModal = () => handleParkingGatewayModal('delete', undefined);

    const openEditParkingGatewayModal = (gateway: ParkingGateways) => handleParkingGatewayModal('edit', gateway, true);
    const closeEditParkingGatewayModal = () => handleParkingGatewayModal('edit', undefined);

    const columns = EntryExitPointsTableColumns({
        openEditParkingGatewayModal,
        openDeleteParkingGatewayModal,
    });

    const { mutate: deleteParkingGateway } = useDeleteParkingGateway({
        onSuccess: (_) => {
            void refetch();
            toast({
                title: formatMessage(parkingManagementMessages.deleteParkingGatewaySuccess),
                description: formatMessage(parkingManagementMessages.deleteParkingGatewaySuccessDetails),
            });
            setSelectedParkingGateway(undefined);
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <>
            <div className="flex flex-col gap-2">
                <GeneralBlockTitle>{formatMessage(parkingManagementMessages.entryExitPoints)}</GeneralBlockTitle>
                <GeneralBlockSubtitle>
                    {formatMessage(parkingManagementMessages.entryExitPointsDescription)}
                </GeneralBlockSubtitle>
            </div>
            <DataTable
                columns={columns}
                data={{ count: tableData.length || 0, results: tableData }}
                semantics={{
                    title: formatMessage(parkingManagementMessages.entryExitPoints),
                    entryName: formatMessage(parkingManagementMessages.entryExitPoint),
                    entryNames: formatMessage(parkingManagementMessages.entryExitPoints),
                }}
                components={{
                    newEntry: AddParkingGateway,
                }}
                enableLocalFilters={true}
            />
            {isDeleteParkingGatewayModalOpen && selectedParkingGateway && (
                <DeleteParkingGatewayModal
                    isDeleteParkingGatewayModalOpen={isDeleteParkingGatewayModalOpen}
                    closeDeleteParkingGatewayModal={closeDeleteParkingGatewayModal}
                    selectedParkingGateway={selectedParkingGateway}
                    deleteParkingGateway={deleteParkingGateway}
                />
            )}
            {isEditParkingGatewayModalOpen && selectedParkingGateway && (
                <EditParkingGatewayModal
                    isEditParkingGatewayModalOpen={isEditParkingGatewayModalOpen}
                    closeEditParkingGatewayModal={closeEditParkingGatewayModal}
                    selectedParkingGateway={selectedParkingGateway}
                    refetch={refetch}
                />
            )}
        </>
    );
};

export default EntryExitPoints;
